import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { useDesignStore } from 'context/DesignContext';
import { fitCameraToSceneObjects} from '../../Utils/ViewerUtils'
import { useViewerStore } from 'context/ViewerContext';

export interface CaptureImageOptions {
  mimeType?: 'image/png' | 'image/jpeg' | 'image/webp';
  quality?: number;
  maxSizeBytes?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export interface MiniViewer3DHandle {
  captureImage: (options?: CaptureImageOptions) => string | null;
  updateMaterialColor: (color: string, target: 'insole' | 'additions') => void;
}

const getDataUrlSize = (dataUrl: string) => {
  const base64 = dataUrl.split(',')[1] ?? '';
  return Math.ceil((base64.length * 3) / 4);
};

const createDataUrl = (canvas: HTMLCanvasElement, mimeType: string, quality?: number) => {
  return mimeType === 'image/png'
    ? canvas.toDataURL(mimeType)
    : canvas.toDataURL(mimeType, quality);
};

const optimizeDataUrl = (
  canvas: HTMLCanvasElement,
  mimeType: string,
  maxSizeBytes: number,
  initialQuality = 0.9
) => {
  let low = 0.3;
  let high = Math.min(0.95, initialQuality);
  let bestDataUrl = createDataUrl(canvas, mimeType, high);

  if (getDataUrlSize(bestDataUrl) <= maxSizeBytes) {
    return bestDataUrl;
  }

  for (let i = 0; i < 6; i += 1) {
    const mid = (low + high) / 2;
    const candidateDataUrl = createDataUrl(canvas, mimeType, mid);
    const candidateSize = getDataUrlSize(candidateDataUrl);

    if (candidateSize <= maxSizeBytes) {
      bestDataUrl = candidateDataUrl;
      low = mid;
    } else {
      high = mid;
    }
  }

  return bestDataUrl;
};

const MiniViewer3D = forwardRef<MiniViewer3DHandle>((props, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const sceneRef = useRef<THREE.Scene>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const controlsRef = useRef<OrbitControls>();
  const isInitializedRef = useRef(false);
  const insoleDesign = useDesignStore.getState().insoleDesign;
  const insoleDefaultColor = useViewerStore.getState().insoleDefaultColor;  

  const meshesRef = useRef<{ insole: THREE.Mesh[], additions: THREE.Mesh[] }>({
    insole: [],
    additions: []
  });

  useEffect(() => {
    if (!containerRef.current || isInitializedRef.current) return;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf1f0f0);
    sceneRef.current = scene;

    // Camera
    const aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
    const camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 2000);
    camera.position.set(0, 1, 1);
    camera.lookAt(0, 0, 0);  
    cameraRef.current = camera;


    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableRotate = false; 
    controls.enableZoom = false;   
    controls.enablePan = false;   
    controls.enabled = false;      
    controls.target.set(0, 0, 0); 
    controlsRef.current = controls;

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.9));

    const mainLight = new THREE.DirectionalLight(0xffffff, 3);
    mainLight.position.set(5, 5, 5);
    mainLight.castShadow = true;
    mainLight.shadow.mapSize.width = 2048;
    mainLight.shadow.mapSize.height = 2048;
    mainLight.shadow.camera.near = 0.5;
    mainLight.shadow.camera.far = 50;
    mainLight.shadow.camera.left = -10;
    mainLight.shadow.camera.right = 10;
    mainLight.shadow.camera.top = 10;
    mainLight.shadow.camera.bottom = -10;
    scene.add(mainLight);

    const bottomLight = new THREE.DirectionalLight(0xffffff, 1);
    bottomLight.position.set(5, -5, 5);
    scene.add(bottomLight);

    const topLight = new THREE.DirectionalLight(0xffffff, 1);
    topLight.position.set(5, 5, 5);
    scene.add(topLight);

    isInitializedRef.current = true;

    insoleDesign?.insoles?.forEach(insole => {
    if (insole.mainMesh) {
      const meshCopy = insole.mainMesh.clone();
      meshCopy.visible = true;

      const originalMat = insole.mainMesh.material as THREE.MeshStandardMaterial;
      
      meshCopy.material = new THREE.MeshStandardMaterial({
        color: `#${insoleDefaultColor || 'ffffff'}`,
        metalness: 0.8,
        roughness: 0.1,
        flatShading: false,
        transparent: false, 
        opacity: 1,          
        side: originalMat?.side ?? THREE.DoubleSide
      });

      meshCopy.castShadow = true;
      meshCopy.receiveShadow = true;

      sceneRef.current?.add(meshCopy);

      if (meshesRef?.current) {
        meshesRef.current.insole = meshesRef.current.insole || [];
        meshesRef.current.insole.push(meshCopy);
      }
    }

    if (insole.additions?.length) {
      insole.additions.forEach(addition => {
        if (addition.viewMesh) {
          const meshAdditionCopy = addition.viewMesh.clone();
          meshAdditionCopy.visible = true;
          const originalMat = addition.viewMesh.material as THREE.MeshStandardMaterial;
          const originalColor = originalMat?.color?.clone?.() || new THREE.Color(0xaaaaaa);

          meshAdditionCopy.material = new THREE.MeshStandardMaterial({
            color: originalColor,
            metalness: 0.8,
            roughness: 0.1,
            transparent: false, 
            opacity: 1,      
            side: originalMat?.side ?? THREE.DoubleSide
          });

          meshAdditionCopy.castShadow = true;
          meshAdditionCopy.receiveShadow = true;

          sceneRef.current?.add(meshAdditionCopy);

          if (meshesRef?.current) {
            meshesRef.current.additions = meshesRef.current.additions || [];
            meshesRef.current.additions.push(meshAdditionCopy);
          }
        }
      });
    }
  });    
  
    fitCameraToSceneObjects(cameraRef.current!, controlsRef.current!, containerRef.current!, sceneRef); 

    const animate = () => {
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        requestAnimationFrame(animate);
        controlsRef.current?.update();
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();

    // Resize handler
    const handleResize = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      controls.update();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      controls.dispose();
      renderer.dispose();
      if (containerRef.current && renderer.domElement.parentElement === containerRef.current) {
        containerRef.current.removeChild(renderer.domElement);
      }
      isInitializedRef.current = false;
      scene.clear();
    };
  }, [insoleDesign]);

  useImperativeHandle(ref, () => ({
  // captureImage: () => {
  //   if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return null;
  //   rendererRef.current.render(sceneRef.current, cameraRef.current);
  //   return rendererRef.current.domElement.toDataURL("image/png");
  // },
  captureImage: (options?: CaptureImageOptions) => {
    if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return null;

    rendererRef.current.render(sceneRef.current, cameraRef.current);

    const sourceCanvas = rendererRef.current.domElement as HTMLCanvasElement;
    const width = sourceCanvas.width;
    const height = sourceCanvas.height;
    const maxWidth = options?.maxWidth ?? width;
    const maxHeight = options?.maxHeight ?? height;
    const scale = Math.min(1, maxWidth / width, maxHeight / height);
    const outputWidth = Math.max(1, Math.round(width * scale));
    const outputHeight = Math.max(1, Math.round(height * scale));

    const captureCanvas = document.createElement('canvas');
    captureCanvas.width = outputWidth;
    captureCanvas.height = outputHeight;

    const ctx = captureCanvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(sourceCanvas, 0, 0, outputWidth, outputHeight);

    let mimeType = options?.mimeType ?? (options?.maxSizeBytes ? 'image/jpeg' : 'image/png');
    if (options?.maxSizeBytes && mimeType === 'image/png') {
      mimeType = 'image/jpeg';
    }
    const quality = options?.quality ?? 0.9;

    let dataUrl = createDataUrl(captureCanvas, mimeType, quality);
    if (options?.maxSizeBytes && getDataUrlSize(dataUrl) > options.maxSizeBytes) {
      dataUrl = optimizeDataUrl(captureCanvas, mimeType, options.maxSizeBytes, quality);
    }

    return dataUrl;
  },
  updateMaterialColor: (
    color: string,
    target: keyof typeof meshesRef.current) => {
    meshesRef.current[target].forEach((mesh: THREE.Mesh) => {
      if (mesh.material instanceof THREE.MeshStandardMaterial) {
        mesh.material.color.set(color);
        mesh.material.needsUpdate = true;
      }
    });
  }
  }));


  return (
    <div
      ref={containerRef}
      style={{
      width: '100%',
      height: '250px',
      borderRadius: 6,
      border: '1px solid #ccc',
      backgroundColor: '#f1f0f0',
      }}
    />
  );
});

export default MiniViewer3D;
